import { Component, OnInit, Input, OnChanges, SimpleChanges, OnDestroy, ViewChild, EventEmitter, Output, HostListener, ChangeDetectorRef } from '@angular/core';
import { ChartDataSets, ChartOptions } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { KnowledgeSpacePoint, PersistenceApiService } from '../../../../shared/services/persistence-api.service';

// --- PLUGIN: Network Lines ---
const networkLinesPlugin = {
  id: 'networkLines',
  beforeDatasetsDraw: (chart: any) => {
    const ctx = chart.ctx;
    const xAxis = chart.scales['x-axis-1'];
    const yAxis = chart.scales['y-axis-1'];
    const dataset = chart.data.datasets[0];

    if (!dataset || !dataset.data || !xAxis || !yAxis) return;

    const centerPoint = dataset.data.find((p: any) => p.isCenter === true);
    if (!centerPoint) return;

    const cx = xAxis.getPixelForValue(centerPoint.x);
    const cy = yAxis.getPixelForValue(centerPoint.y);

    ctx.save();
    dataset.data.forEach((point: any, i: number) => {
      if (point.isCenter || point.isHidden) return;

      const x = xAxis.getPixelForValue(point.x);
      const y = yAxis.getPixelForValue(point.y);
      const score = point.score || 0;

      const bgColor = Array.isArray(dataset.pointBackgroundColor) ? dataset.pointBackgroundColor[i] : dataset.pointBackgroundColor;

      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(x, y);
      ctx.lineWidth = 1 + (score * 2);

      if (typeof bgColor === 'string' && bgColor.includes('0.2')) {
        ctx.strokeStyle = 'rgba(200,200,200,0.05)';
      } else {
        ctx.strokeStyle = 'rgba(100, 116, 139, 0.3)';
      }
      ctx.stroke();
    });
    ctx.restore();
  }
};

interface FileStat {
  name: string;
  count: number;
  color: string;
  percentage: number;
}

interface RenderPoint {
  x: number;
  y: number;
  rawId: string;
  title: string;
  fileName: string;
  score: number;
  isCenter: boolean;
  baseColor: string;
}

@Component({
  selector: 'app-vector-space',
  templateUrl: './vector-space.component.html',
  styleUrls: ['./vector-space.component.scss']
})
export class VectorSpaceComponent implements OnInit, OnChanges, OnDestroy {
  @Input() highlightIds: string[] = [];
  @Input() similarityScores: { [id: string]: number } = {};
  @Input() searchItems: any[] = [];
  @Input() threshold: number = 0;
  @Input() projectName: string | null = null;
  @Input() filterByFileId: string | null | undefined = null;
  @Output() nodeClicked = new EventEmitter<string>();

  @ViewChild(BaseChartDirective) chart: BaseChartDirective | undefined;

  public scatterChartOptions: ChartOptions = {};
  public scatterChartDatasets: ChartDataSets[] = [];
  public scatterChartPlugins = [networkLinesPlugin];

  // STATE
  isLoading = false;
  selectedNodeId: string | null = null;
  selectedNodeTitle: string | null = null;

  // LEGEND STATE
  isLegendExpanded = true;
  fileStats: FileStat[] = [];
  hoveredFile: string | null = null;
  activeFileFilter: string | null = null;

  // PANNING & ZOOM STATE
  isDragging = false;
  hasUserPanned = false;
  private wasDragging = false;
  private dragStart = { x: 0, y: 0 };
  private currentZoom = 1;

  // Store original axis bounds
  private originalBounds = {
    xMin: undefined as number | undefined,
    xMax: undefined as number | undefined,
    yMin: undefined as number | undefined,
    yMax: undefined as number | undefined
  };

  protected originalDataPoints: KnowledgeSpacePoint[] = [];
  private calculatedPoints: RenderPoint[] = [];
  private destroy$ = new Subject<void>();

  private fileColors = ['#4f46e5', '#10b981', '#f59e0b', '#ec4899', '#06b6d4', '#8b5cf6', '#f97316', '#64748b', '#dc2626', '#84cc16'];

  constructor(
    private kbService: PersistenceApiService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.setupChartOptions();
    if (this.projectName) {
      this.loadVisualizationData();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['projectName'] && !changes['projectName'].firstChange) ||
      (changes['filterByFileId'] && !changes['filterByFileId'].firstChange)) {
      this.loadVisualizationData();
      this.resetView();
    }
    else if (changes['searchItems'] || changes['threshold']) {
      this.calculateLayout();
      this.updateChartVisuals();
      // Don't reset view on search updates - maintain user's pan/zoom
    }
    else if (changes['highlightIds']) {
      this.updateChartVisuals();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // --- PANNING LOGIC ---

  startDrag(event: MouseEvent): void {
    if (event.button !== 0 || !this.chart || !this.chart.chart) return;
    this.isDragging = true;
    this.wasDragging = false;
    this.dragStart = { x: event.clientX, y: event.clientY };
    event.preventDefault();
  }

  @HostListener('window:mousemove', ['$event'])
  onDrag(event: MouseEvent): void {
    if (!this.isDragging || !this.chart || !this.chart.chart) return;

    const dxPx = this.dragStart.x - event.clientX;
    const dyPx = this.dragStart.y - event.clientY;

    if (Math.abs(dxPx) > 3 || Math.abs(dyPx) > 3) {
      this.wasDragging = true;
    }

    const chart = this.chart.chart as any;
    const xAxis = chart.scales['x-axis-1'];
    const yAxis = chart.scales['y-axis-1'];

    if (!xAxis || !yAxis) return;

    // Calculate scaling factors
    const xRange = xAxis.max - xAxis.min;
    const yRange = yAxis.max - yAxis.min;
    const xFactor = xAxis.width > 0 ? (xRange / xAxis.width) : 0;
    const yFactor = yAxis.height > 0 ? (yRange / yAxis.height) : 0;
    const dxUnits = xFactor * dxPx;
    const dyUnits = yFactor * -dyPx;

    // Save original bounds if this is the first move
    if (this.originalBounds.xMin === undefined) {
      this.originalBounds.xMin = xAxis.min;
      this.originalBounds.xMax = xAxis.max;
      this.originalBounds.yMin = yAxis.min;
      this.originalBounds.yMax = yAxis.max;
    }

    // Calculate new bounds
    const currentXMin = xAxis.options.ticks.min !== undefined ? xAxis.options.ticks.min : xAxis.min;
    const currentXMax = xAxis.options.ticks.max !== undefined ? xAxis.options.ticks.max : xAxis.max;
    const currentYMin = yAxis.options.ticks.min !== undefined ? yAxis.options.ticks.min : yAxis.min;
    const currentYMax = yAxis.options.ticks.max !== undefined ? yAxis.options.ticks.max : yAxis.max;

    const newXMin = currentXMin + dxUnits;
    const newXMax = currentXMax + dxUnits;
    const newYMin = currentYMin + dyUnits;
    const newYMax = currentYMax + dyUnits;

    // 1. Update Native Chart Instance (Immediate visual update)
    xAxis.options.ticks.min = newXMin;
    xAxis.options.ticks.max = newXMax;
    yAxis.options.ticks.min = newYMin;
    yAxis.options.ticks.max = newYMax;

    // 2. CRITICAL: Sync Angular Component Options
    // This stops the chart from resetting/flickering when Angular Change Detection runs
    if (this.scatterChartOptions.scales && this.scatterChartOptions.scales.xAxes && this.scatterChartOptions.scales.yAxes) {
      this.scatterChartOptions.scales.xAxes[0].ticks.min = newXMin;
      this.scatterChartOptions.scales.xAxes[0].ticks.max = newXMax;
      this.scatterChartOptions.scales.yAxes[0].ticks.min = newYMin;
      this.scatterChartOptions.scales.yAxes[0].ticks.max = newYMax;
    }

    this.dragStart = { x: event.clientX, y: event.clientY };
    this.hasUserPanned = true;

    // 3. Update without animation
    chart.update({ duration: 0 });
  }

  @HostListener('window:mouseup')
  endDrag(): void {
    this.isDragging = false;
  }

  // --- ZOOM LOGIC ---

  @HostListener('wheel', ['$event'])
  onWheel(event: WheelEvent): void {
    if (!this.chart || !this.chart.chart) return;

    event.preventDefault();

    const chart = this.chart.chart as any;
    const xAxis = chart.scales['x-axis-1'];
    const yAxis = chart.scales['y-axis-1'];

    if (!xAxis || !yAxis) return;

    if (this.originalBounds.xMin === undefined) {
      this.originalBounds.xMin = xAxis.min;
      this.originalBounds.xMax = xAxis.max;
      this.originalBounds.yMin = yAxis.min;
      this.originalBounds.yMax = yAxis.max;
    }

    const zoomIntensity = 0.1;
    const delta = -event.deltaY / 100;
    const scale = Math.exp(delta * zoomIntensity);

    this.currentZoom *= scale;

    // Limit zoom
    if (this.currentZoom < 0.1) { this.currentZoom = 0.1; return; }
    if (this.currentZoom > 10) { this.currentZoom = 10; return; }

    const rect = (event.target as HTMLElement).getBoundingClientRect();
    const mouseX = event.clientX - rect.left;
    const mouseY = event.clientY - rect.top;

    const xValue = xAxis.getValueForPixel(mouseX);
    const yValue = yAxis.getValueForPixel(mouseY);

    const currentXMin = xAxis.options.ticks.min !== undefined ? xAxis.options.ticks.min : xAxis.min;
    const currentXMax = xAxis.options.ticks.max !== undefined ? xAxis.options.ticks.max : xAxis.max;
    const currentYMin = yAxis.options.ticks.min !== undefined ? yAxis.options.ticks.min : yAxis.min;
    const currentYMax = yAxis.options.ticks.max !== undefined ? yAxis.options.ticks.max : yAxis.max;

    const xRange = currentXMax - currentXMin;
    const yRange = currentYMax - currentYMin;

    const newXRange = xRange / scale;
    const newYRange = yRange / scale;

    const xRatio = (xValue - currentXMin) / xRange;
    const yRatio = (yValue - currentYMin) / yRange;

    const newXMin = xValue - (newXRange * xRatio);
    const newXMax = xValue + (newXRange * (1 - xRatio));
    const newYMin = yValue - (newYRange * yRatio);
    const newYMax = yValue + (newYRange * (1 - yRatio));

    // 1. Update Native Chart
    xAxis.options.ticks.min = newXMin;
    xAxis.options.ticks.max = newXMax;
    yAxis.options.ticks.min = newYMin;
    yAxis.options.ticks.max = newYMax;

    // 2. Sync Angular Component Options
    if (this.scatterChartOptions.scales && this.scatterChartOptions.scales.xAxes && this.scatterChartOptions.scales.yAxes) {
      this.scatterChartOptions.scales.xAxes[0].ticks.min = newXMin;
      this.scatterChartOptions.scales.xAxes[0].ticks.max = newXMax;
      this.scatterChartOptions.scales.yAxes[0].ticks.min = newYMin;
      this.scatterChartOptions.scales.yAxes[0].ticks.max = newYMax;
    }

    this.hasUserPanned = true;
    chart.update({ duration: 0 });
  }

  resetView(): void {
    this.hasUserPanned = false;
    this.currentZoom = 1;
    this.originalBounds = {
      xMin: undefined, xMax: undefined, yMin: undefined, yMax: undefined
    };

    // 1. Reset Native Chart Options
    if (this.chart && this.chart.chart) {
      const chart = this.chart.chart as any;
      const xAxis = chart.scales['x-axis-1'];
      const yAxis = chart.scales['y-axis-1'];

      if (xAxis && yAxis) {
        delete xAxis.options.ticks.min;
        delete xAxis.options.ticks.max;
        delete yAxis.options.ticks.min;
        delete yAxis.options.ticks.max;
      }
    }

    // 2. Reset Angular Component Options
    // This ensures that when the chart updates, it doesn't snap back to the zoomed state
    if (this.scatterChartOptions.scales && this.scatterChartOptions.scales.xAxes && this.scatterChartOptions.scales.yAxes) {
      this.scatterChartOptions.scales.xAxes[0].ticks.min = undefined;
      this.scatterChartOptions.scales.xAxes[0].ticks.max = undefined;
      this.scatterChartOptions.scales.yAxes[0].ticks.min = undefined;
      this.scatterChartOptions.scales.yAxes[0].ticks.max = undefined;
    }

    // 3. Update with animation for smooth reset
    if (this.chart && this.chart.chart) {
      this.chart.chart.update({ duration: 750 });
    }
  }

  // --- LEGEND & LAYOUT ---

  toggleLegend(): void {
    this.isLegendExpanded = !this.isLegendExpanded;
  }

  calculateLayout(): void {
    this.calculatedPoints = [];
    const newStats: FileStat[] = [];
    const isSearchMode = this.searchItems && this.searchItems.length > 0;

    const statsMap = new Map<string, number>();
    const colorMap = new Map<string, string>();
    let colorIdx = 0;

    const getColor = (fname: string) => {
      if (!colorMap.has(fname)) {
        colorMap.set(fname, this.fileColors[colorIdx % this.fileColors.length]);
        colorIdx++;
      }
      return colorMap.get(fname)!;
    };

    if (isSearchMode) {
      this.calculatedPoints.push({
        x: 0, y: 0, isCenter: true, rawId: 'center',
        title: 'Search Query', fileName: '', score: 1, baseColor: '#1e293b'
      });

      const validItems = (this.searchItems || []).filter(item => {
        // HANDLE NESTING: The API returns { item: {...}, score: ... }
        // We need to check if 'item' has a property called 'item' (from Python KBSearchResult)
        // or if it is the item itself.
        const actualNode = item.item ? item.item : item;

        // Handle ID (MongoDB _id or id)
        const id = String(actualNode.id || actualNode._id || actualNode.kb_id);

        // Handle Score (it might be on the wrapper or passed separately)
        const score = item.score !== undefined ? item.score : (this.similarityScores[id] || 0);

        return score >= this.threshold;
      });

      // Sort
      validItems.sort((a, b) => {
        const nodeA = a.item ? a.item : a;
        const nodeB = b.item ? b.item : b;
        return (nodeA.source_file_name || 'z').localeCompare(nodeB.source_file_name || 'z');
      });

      const totalPoints = validItems.length;

      validItems.forEach((wrapper, index) => {
        // 1. EXTRACT DATA SAFELY
        const actualNode = wrapper.item ? wrapper.item : wrapper;

        const id = String(actualNode.id || actualNode._id || actualNode.kb_id);
        const score = wrapper.score !== undefined ? wrapper.score : (this.similarityScores[id] || 0);

        // 2. CRITICAL: READ FILE NAME
        // Check source_file_name, then filename, then fall back to Unknown
        const fname = actualNode.source_file_name || actualNode.filename || 'Unknown Source';

        // 3. Update Stats Map for Legend
        statsMap.set(fname, (statsMap.get(fname) || 0) + 1);
        const color = getColor(fname);

        const r = 0.2 + (Math.pow(1 - score, 3) * 12);
        const sectorAngle = (2 * Math.PI) / (totalPoints || 1);
        const theta = (index * sectorAngle) + ((Math.random() - 0.5) * 0.2);

        this.calculatedPoints.push({
          x: r * Math.cos(theta),
          y: r * Math.sin(theta),
          rawId: id,
          title: actualNode.title || actualNode.term, // Use actualNode here
          fileName: fname, // Use extracted fname
          score: score,
          isCenter: false,
          baseColor: color
        });
      });

      statsMap.forEach((count, name) => {
        newStats.push({
          name: name,
          count: count,
          color: colorMap.get(name)!,
          percentage: totalPoints > 0 ? (count / totalPoints) * 100 : 0
        });
      });

    } else {
      // DEFAULT VIEW
      if (this.originalDataPoints && this.originalDataPoints.length > 0) {
        this.originalDataPoints.forEach(p => {
          const meta = (p as any).metadata;
          const fname = meta?.source_file_name || 'Unknown Source';

          statsMap.set(fname, (statsMap.get(fname) || 0) + 1);
          const color = getColor(fname);

          this.calculatedPoints.push({
            x: p.x,
            y: p.y,
            rawId: p.id,
            title: p.title,
            fileName: fname,
            score: 0,
            isCenter: false,
            baseColor: color
          });
        });

        const totalPoints = this.originalDataPoints.length;
        statsMap.forEach((count, name) => {
          newStats.push({
            name: name,
            count: count,
            color: colorMap.get(name)!,
            percentage: totalPoints > 0 ? (count / totalPoints) * 100 : 0
          });
        });
      }
    }

    newStats.sort((a, b) => b.count - a.count);
    this.fileStats = newStats;
    this.cdr.detectChanges();
  }

  loadVisualizationData(): void {
    if (!this.projectName) return;

    this.isLoading = true;

    this.kbService.getKnowledgeSpace(this.projectName, this.filterByFileId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (points) => {
          this.isLoading = false;

          if (Array.isArray(points) && points.length > 0) {
            this.originalDataPoints = points;
            this.calculateLayout();
            this.updateChartVisuals();
          } else {
            this.originalDataPoints = [];
            this.fileStats = [];
            this.cdr.detectChanges();
          }
        },
        error: (err) => {
          console.error('Error loading visualization data:', err);
          this.isLoading = false;
          this.originalDataPoints = [];
          this.fileStats = [];
          this.cdr.detectChanges();
        }
      });
  }

  updateChartVisuals(): void {
    if (this.calculatedPoints.length === 0) {
      return;
    }

    const datasetData: any[] = [];
    const bgColors: string[] = [];
    const borderColors: string[] = [];
    const radii: number[] = [];
    const hoverRadii: number[] = [];

    const activeTarget = this.hoveredFile || this.activeFileFilter;

    this.calculatedPoints.forEach(p => {
      const isMatch = !activeTarget || p.fileName === activeTarget;
      const isSelected = this.selectedNodeId === p.rawId;
      const isDimmed = !p.isCenter && !isMatch;

      if (p.isCenter) {
        bgColors.push('#0f172a');
        radii.push(20);
        hoverRadii.push(22);
        borderColors.push('#fff');
      } else if (isDimmed) {
        bgColors.push('rgba(203, 213, 225, 0.2)');
        radii.push(3);
        hoverRadii.push(3);
        borderColors.push('transparent');
      } else {
        bgColors.push(p.baseColor);
        const baseSize = 6 + (p.score * 8);
        radii.push(isSelected ? baseSize + 4 : baseSize);
        hoverRadii.push(baseSize + 4);
        borderColors.push(isSelected ? '#000' : '#fff');
      }

      datasetData.push({ ...p, isHidden: isDimmed });
    });

    this.scatterChartDatasets = [{
      data: datasetData,
      pointBackgroundColor: bgColors,
      pointBorderColor: borderColors,
      pointRadius: radii,
      pointHoverRadius: hoverRadii,
      borderWidth: 1.5
    }];

    if (this.chart && this.chart.chart) {
      // FIXED LINE BELOW
      this.chart.chart.update({ duration: 0 });
    }
  }

  // --- INTERACTION ---

  onLegendClick(name: string) {
    this.activeFileFilter = (this.activeFileFilter === name) ? null : name;
    this.updateChartVisuals();
  }

  onLegendHover(name: string) {
    this.hoveredFile = name;
    this.updateChartVisuals();
  }

  onLegendLeave() {
    this.hoveredFile = null;
    this.updateChartVisuals();
  }

  clearSelection() {
    this.selectedNodeId = null;
    this.updateChartVisuals();
  }

  requestOpenModal() {
    if (this.selectedNodeId) this.nodeClicked.emit(this.selectedNodeId);
  }

  handlePointSelection(id: string, title: string) {
    this.selectedNodeId = (this.selectedNodeId === id) ? null : id;
    this.selectedNodeTitle = title;
    this.updateChartVisuals();
  }

  setupChartOptions(): void {
    this.scatterChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 0 },
      legend: { display: false },
      title: { display: false },
      layout: { padding: 20 },
      events: ['mousemove', 'mouseout', 'click', 'touchstart', 'touchmove'],
      scales: {
        xAxes: [{
          id: 'x-axis-1',
          display: false,
          gridLines: { display: false },
          ticks: { display: false, min: undefined, max: undefined } // Initialize these
        }],
        yAxes: [{
          id: 'y-axis-1',
          display: false,
          gridLines: { display: false },
          ticks: { display: false, min: undefined, max: undefined } // Initialize these
        }]
      },
      tooltips: {
        // ... (keep your existing tooltip config)
        enabled: true,
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        titleFontColor: '#1e293b',
        bodyFontColor: '#475569',
        borderColor: '#e2e8f0',
        borderWidth: 1,
        cornerRadius: 4,
        xPadding: 10,
        yPadding: 10,
        displayColors: false,
        filter: (item, data) => {
          const p = data.datasets![item.datasetIndex!].data![item.index!] as any;
          return !p.isHidden;
        },
        callbacks: {
          label: (item, data) => {
            const p = data.datasets![item.datasetIndex!].data![item.index!] as any;
            if (p.isCenter) return 'Search Query';
            return `${p.title} (${Math.round((p.score || 0) * 100)}%)`;
          },
          afterLabel: (item, data) => {
            const p = data.datasets![item.datasetIndex!].data![item.index!] as any;
            return p.isCenter ? '' : p.fileName;
          }
        }
      },
      onClick: (evt, activeElements) => {
        if (this.wasDragging) {
          this.wasDragging = false;
          return;
        }

        this.isDragging = false;

        if (activeElements && activeElements.length > 0) {
          const idx = (activeElements[0] as any)._index;
          const point = this.calculatedPoints[idx];
          const visualData = this.chart?.datasets[0].data?.[idx] as any;

          if (point && !point.isCenter && (!visualData || !visualData.isHidden)) {
            this.handlePointSelection(point.rawId, point.title);
          }
        } else {
          this.clearSelection();
        }
      },
      onHover: (event: MouseEvent, activeElements: Array<any>) => {
        const target = event.target as HTMLElement;
        if (this.isDragging) {
          target.style.cursor = 'grabbing';
          return;
        }
        if (activeElements && activeElements.length > 0) {
          const index = (activeElements[0] as any)._index;
          const point = this.chart?.datasets[0]?.data?.[index] as any;
          if (point && !point.isHidden) {
            target.style.cursor = 'pointer';
          } else {
            target.style.cursor = 'grab';
          }
        } else {
          target.style.cursor = 'grab';
        }
      }
    };
  }
}
